'use client';

/**
 * usePaymentMethodAvailability Hook
 *
 * Checks device availability for digital wallets (Apple Pay, Google Pay).
 * Returns which payment methods are available on the current device.
 *
 * Apple Pay: Available on Safari (macOS/iOS) or via Adyen SDK cross-browser
 * Google Pay: Available on most browsers with Google Pay enabled
 */

import { useCallback, useEffect, useState } from 'react';

import { ADYEN_ANALYTICS_CONFIG, checkApplePayAvailability } from '@/lib/checkout/payments/adyen';
import { PAYMENT_TYPES, type PaymentType } from '@/lib/checkout/payments/types';
import { devError, devLog } from '@/lib/utils/devLog';

interface PaymentMethodAvailability {
  [key: string]: boolean;
}

interface UsePaymentMethodAvailabilityResult {
  availability: PaymentMethodAvailability;
  isLoading: boolean;
  isMethodAvailable: (type: PaymentType) => boolean;
  refresh: () => Promise<void>;
}

// Cache availability results to avoid repeated checks
let cachedAvailability: PaymentMethodAvailability | null = null;
// Promise that resolves when the check completes - allows multiple instances to wait
let checkPromise: Promise<PaymentMethodAvailability> | null = null;

/**
 * Check if Google Pay is available on the device
 *
 * NOTE: This function is currently unused for regular checkout (Google Pay is always shown).
 * Kept for potential use in Express Checkout where we need to know availability
 * before showing the wallet button.
 */
async function _checkGooglePayAvailability(): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  try {
    // Check if Google Pay API is available
    // oxlint-disable-next-line typescript/no-explicit-any
    const googlePayClient = (window as any).google?.payments?.api?.PaymentsClient;
    if (!googlePayClient) {
      // Google Pay script not loaded, try Adyen's check
      return await checkGooglePayViaAdyen();
    }

    const client = new googlePayClient({ environment: 'TEST' });
    const isReadyToPayRequest = {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
            allowedCardNetworks: ['AMEX', 'DISCOVER', 'MASTERCARD', 'VISA'],
          },
        },
      ],
    };

    const response = await client.isReadyToPay(isReadyToPayRequest);
    return response.result === true;
  } catch {
    return await checkGooglePayViaAdyen();
  }
}

/**
 * Check Google Pay availability via Adyen SDK
 */
async function checkGooglePayViaAdyen(): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  try {
    // oxlint-disable-next-line typescript/no-explicit-any
    const AdyenCheckoutModule = (await import('@adyen/adyen-web')) as any;
    const AdyenCheckout = AdyenCheckoutModule.default ?? AdyenCheckoutModule.AdyenCheckout;
    const GooglePay = AdyenCheckoutModule.GooglePay;

    const ADYEN_ENV = process.env.NEXT_PUBLIC_ADYEN_ENV || 'test';
    const ADYEN_CLIENT_KEY = process.env.NEXT_PUBLIC_ADYEN_CLIENT_KEY || '';

    const checkout = await AdyenCheckout({
      environment: ADYEN_ENV,
      clientKey: ADYEN_CLIENT_KEY,
      analytics: ADYEN_ANALYTICS_CONFIG,
      countryCode: 'US',
      amount: { value: 0, currency: 'USD' },
    });

    const googlePay = new GooglePay(checkout, {
      environment: process.env.NEXT_PUBLIC_GOOGLE_PAY_ENV || 'TEST',
    });

    await googlePay.isAvailable();
    return true;
  } catch {
    return false;
  }
}

/**
 * Check all payment method availability
 *
 * Note: Both Apple Pay and Google Pay are always shown in the payment method list.
 * Adyen handles actual availability at runtime:
 * - Native Apple Pay on Safari/iOS
 * - QR code flow for Apple Pay on Chrome/Firefox/Edge on macOS
 * - Google Pay via browser Payment Request API
 *
 * If the payment method isn't actually available, Adyen will show an appropriate
 * error or the component won't render.
 */
async function checkAllAvailability(): Promise<PaymentMethodAvailability> {
  // Check Apple Pay availability - but log the result for debugging
  const applePayAvailable = await checkApplePayAvailability();
  devLog('[PaymentAvailability] Apple Pay available:', applePayAvailable);

  return {
    // Apple Pay - show if Adyen says it's available (includes QR code flow)
    // Always show to allow Adyen's QR code flow on non-Safari browsers
    [PAYMENT_TYPES.APPLE_PAY]: true,
    [PAYMENT_TYPES.APPLE_PAY_EXPRESS]: applePayAvailable, // Express requires native
    // Google Pay is always shown - Adyen handles availability at runtime
    [PAYMENT_TYPES.GOOGLE_PAY]: true,
    [PAYMENT_TYPES.PAY_WITH_GOOGLE]: true,
    // PayPal is always available (web-based)
    [PAYMENT_TYPES.PAYPAL]: true,
    [PAYMENT_TYPES.PAYPAL_EXPRESS]: true,
    // Card payments are always available
    [PAYMENT_TYPES.CARD]: true,
    [PAYMENT_TYPES.SCHEME]: true,
    [PAYMENT_TYPES.VISA]: true,
  };
}

/**
 * Hook to check payment method availability on the current device
 */
export function usePaymentMethodAvailability(): UsePaymentMethodAvailabilityResult {
  const [availability, setAvailability] = useState<PaymentMethodAvailability>(
    cachedAvailability ?? {}
  );
  const [isLoading, setIsLoading] = useState(!cachedAvailability);

  const checkAvailability = useCallback(async () => {
    // Return cached result if available
    if (cachedAvailability) {
      setAvailability(cachedAvailability);
      setIsLoading(false);
      return;
    }

    // If a check is already in progress, wait for it
    if (checkPromise) {
      try {
        const result = await checkPromise;
        setAvailability(result);
      } catch {
        setAvailability({});
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Start new check
    setIsLoading(true);

    checkPromise = checkAllAvailability();

    try {
      const result = await checkPromise;
      cachedAvailability = result;
      setAvailability(result);
    } catch (error) {
      devError('[PaymentAvailability] Error checking availability:', error);
      // Default to showing all methods if check fails
      setAvailability({});
    } finally {
      setIsLoading(false);
      checkPromise = null;
    }
  }, []);

  useEffect(() => {
    checkAvailability();
  }, [checkAvailability]);

  /**
   * Check if a specific payment method is available
   * Returns true if:
   * - The method is not in our availability map (unknown methods are shown)
   * - The method is explicitly available
   */
  const isMethodAvailable = useCallback(
    (type: PaymentType): boolean => {
      // If still loading or method not in map, default to available
      if (isLoading || !(type in availability)) {
        return true;
      }
      return availability[type] === true;
    },
    [availability, isLoading]
  );

  /**
   * Force refresh availability (clears cache)
   */
  const refresh = useCallback(async () => {
    cachedAvailability = null;
    await checkAvailability();
  }, [checkAvailability]);

  return {
    availability,
    isLoading,
    isMethodAvailable,
    refresh,
  };
}

/**
 * Clear the availability cache (useful for testing)
 */
export function clearAvailabilityCache(): void {
  cachedAvailability = null;
  checkPromise = null;
}
